import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { getLibraryAdminSession } from "@libraryadmin/services/auth";
import { useState } from "react";
import { Plus, Loader2, Shield, Users, Eye, EyeOff } from "lucide-react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { useToast } from "@jambushrushti/hooks/use-toast";
import { canManagePortalUsers } from "@/services/jambushrustiAuth";
import { getAdminSession } from "@jambushrushti/services/adminAuth";
import { createUser as signupJambushrusti } from "@jambushrushti/services/portalUsers";
import { UsersDirectory } from "@jambushrushti/components/admin/UsersDirectory";
import { Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

type PortalRole = "cms" | "library" | "admin";
const roles: { value: PortalRole; label: string; description: string }[] = [
  {
    value: "cms",
    label: "CMS",
    description: "Manage CMS content.",
  },
  {
    value: "library",
    label: "Library",
    description: "Manage the Library. Public CMS content remains readable.",
  },
  {
    value: "admin",
    label: "Admin",
    description: "Full access to CMS and Library.",
  },
];
const permissions = [
  {
    name: "Manage users and roles",
    cms: "Not allowed",
    library: "Not allowed",
    admin: "Allowed",
  },
  {
    name: "Read public CMS content",
    cms: "Allowed",
    library: "Allowed",
    admin: "Allowed",
  },
  {
    name: "Create, edit and delete CMS content",
    cms: "Allowed",
    library: "Not allowed",
    admin: "Allowed",
  },
  {
    name: "Read and manage Library records",
    cms: "Not allowed",
    library: "Allowed",
    admin: "Allowed",
  },
];

export default function UsersManagement({
  portal = "cms",
}: {
  portal?: "cms" | "library";
}) {
  return canManagePortalUsers(
    portal === "library" ? getLibraryAdminSession() : getAdminSession()
  ) ? (
    <UsersManagementContent portal={portal} />
  ) : (
    <Navigate
      to={portal === "library" ? "/library-admin" : "/admin/dashboard"}
      replace
    />
  );
}
function UsersManagementContent({ portal }: { portal: "cms" | "library" }) {
  const Layout = portal === "library" ? DashboardLayout : AdminLayout;
  const title = portal === "library" ? "Roles & Permissions" : "Users & Roles";
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const changeOpen = (value: boolean) => {
    if (saving) {
      return;
    }
    setOpen(value);
    if (!value) {
      setEmail("");
      setPassword("");
      setShowPassword(false);
    }
  };

  const createUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) || !password.trim()) {
      toast({
        title: "Check user details",
        description: "Enter a valid email address and a password.",
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      await signupJambushrusti(email.trim(), password, portal, portal);
      void queryClient.invalidateQueries({ queryKey: ["portal-users"] });
      toast({
        title: "User created",
        description: `${email.trim()} now has the ${portal === "library" ? "Library" : "CMS"} role.`,
      });
      setOpen(false);
      setEmail("");
      setPassword("");
      setShowPassword(false);
    } catch (error) {
      toast({
        title: "Unable to create user",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title={title}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{title}</h1>
            <p className="text-muted-foreground">
              Manage access to CMS and Library.
            </p>
          </div>
          <Dialog open={open} onOpenChange={changeOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add User</DialogTitle>
                <DialogDescription>
                  Create an account and choose its portal access.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createUser} noValidate className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-user-email">Email</Label>
                  <Input
                    id="new-user-email"
                    type="email"
                    autoComplete="off"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    disabled={saving}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-password">Initial password</Label>
                  <div className="relative">
                    <Input
                      id="new-user-password"
                      type={showPassword ? "text" : "password"}
                      className="pr-11"
                      autoComplete="new-password"
                      value={password}
                      onChange={event => setPassword(event.target.value)}
                      disabled={saving}
                      required
                    />
                    <button
                      type="button"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                      disabled={saving}
                      onClick={() => setShowPassword(current => !current)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-stone-500 transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-user-role">Role</Label>
                  <Select value={portal} disabled>
                    <SelectTrigger id="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={portal}>
                        {portal === "library" ? "Library" : "CMS"}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => changeOpen(false)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {saving ? "Creating..." : "Create User"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <UsersDirectory portal={portal} />
      </div>
    </Layout>
  );
}
